<template>
    <div>
        <div class="mb-3 text-right">
            <hts-form-button
                v-if="checkPermission(layoutPermissions.vehicle_add) || checkPermission(layoutPermissions.vehicle_administration)"
                color="main"
                icon-name="hi-plus-circle-icon"
                icon-position="right"
                type="submit"
                @click="handleAddVehicle">
                {{ $t('labels.addVehicle') }}
            </hts-form-button>
        </div>
        <hts-data-table
            ref="dataTables"
            prefix-module="vehicle"
            :headers="headers"
            :server="apiListVehicles"
            :query-extend="{ type: 1 }"
            :use-archive="apiArchive"
            :use-restore="apiRestore"
            use-selection
            store-name="vehicleDataTable"
            store-key="Vehicle" />
        <document
            ref="showDrawerDocumentVehicle"
            type="vehicle"
            :reload="isReload" />
        <add-vehicle
            v-if="checkPermission(layoutPermissions.vehicle_add) || checkPermission(layoutPermissions.vehicle_administration)"
            ref="showDrawerAddVehicle"
            :column="2"
            @refresh-data-tables="refreshDataTables()" />
        <list-plugin
            v-if="checkPermission(layoutPermissions.vehicle_plugin_management)"
            ref="showDrawerListVehiclePlugin"
            :reload="isReload" />
        <edit-vehicle
            v-if="checkPermission(layoutPermissions.vehicle_edit) || checkPermission(layoutPermissions.vehicle_administration)"
            ref="showDrawerEditVehicle"
            :column="2"
            @refresh-data-tables="refreshDataTables()" />
        <hts-modal
            :show="showPopupDeleteVehicle"
            :title="$t('labels.deleteVehicle')"
            type="confirm"
            :content="$t('labels.confirmDeleteVehicle')"
            @close="closePopupDeleteVehicle"
            @confirm="() => actionDeleteVehicle()" />
        <detail-vehicle
            ref="showDrawerListVehicleDetail"
            :vehicle-id="vehicleId"
            :column="2"
            :list-type-vehicles="listTypeVehicles"
            @refresh-data-tables="refreshDataTables()" />
        <hts-modal
            :show="showPopupChangeStatusVehicle"
            :title="$t('labels.statusChanges')"
            type="confirm"
            :content="$t('labels.statusChangesContent')"
            @hide="showPopupChangeStatusVehicle = false"
            @confirm="() => actionChangesStatusVehicle()" />
    </div>
</template>

<script lang="ts" setup>
import Actions from './ignore/actions.vue'
import AddVehicle from './ignore/add-vehicle.vue'
import DetailVehicle from './ignore/detail-vehicle.vue'
import Document from './ignore/document/document.vue'
import EditVehicle from './ignore/edit-vehicle.vue'
import { IVehicle } from './ignore/imports/form'
import ListPlugin from './ignore/plugin/list-plugin.vue'
import { checkPermission } from '@/utils/permission-check'
import layoutPermissions from '@/data/permissions/layout-permissions'

const { $formatters } = useNuxtApp()
const route = useRoute()
// onBeforeMount(() => {
//   route.meta.pageTabs = pageTabs
// })

const apiArchive = computed(() => {
  return { api: $urlPartner.vehicle.vehicle.archive, permissions: checkPermission(layoutPermissions.vehicle_archive) }
})

const apiRestore = computed(() => {
  return { api: $urlPartner.vehicle.vehicle.restore, permissions: checkPermission(layoutPermissions.vehicle_restore) }
})

interface ITypeVehicle {
  code: string
  name: string
}

const listTypeVehicles = ref<ITypeVehicle[]>([])
const showDrawerAddVehicle: any = ref()
const showDrawerEditVehicle: any = ref()
const showDrawerDocumentVehicle: any = ref()
const showDrawerAddDocumentVehicle: any = ref()
const showDrawerListVehiclePlugin: any = ref()
const showDrawerListVehicleDetail: any = ref()
const vehicleId = ref()
const dataTables = ref()
const checkDisabledEdit = ref(true)
const showPopupDeleteVehicle = ref(false)
const vehicleIsSelectedToDelete = ref('')
const isReload = ref(false)
const dataChangeStatus = ref()
const showPopupChangeStatusVehicle = ref(false)
const valueStatus = ref()

const apiListVehicles = $urlPartner.vehicle.vehicle['get-list']

const handleViewDetail = (vehicle: any) => {
  if (!vehicle) {
    return null
  }
  vehicleId.value = vehicle._id
  if (showDrawerListVehicleDetail.value) {
    showDrawerListVehicleDetail.value.openEditVehicle(vehicle)
  }
}
const handleViewDocument = (vehicle: any) => {
  if (!vehicle) {
    return null
  }
  vehicleId.value = vehicle._id
  if (showDrawerDocumentVehicle.value) {
    showDrawerDocumentVehicle.value.openDrawerDocument(vehicle._id)
  }
}

const handleAddDocument = (vehicle: any) => {
  if (!vehicle) {
    return null
  }
  vehicleId.value = vehicle._id
  if (showDrawerAddDocumentVehicle.value) {
    showDrawerAddDocumentVehicle.value.openAddDocument()
  }
}
const handleEditVehicle = (vehicle: IVehicle) => {
  if (!vehicle) {
    return null
  }
  const id: string = route.query.id ? String(route.query.id) : ''
  if (id && vehicle._id && vehicle._id !== id) {
    checkDisabledEdit.value = true
  }
  if (id && vehicle._id && vehicle._id === id) {
    checkDisabledEdit.value = false
  }
  showDrawerEditVehicle.value.openEditVehicle(vehicle)
}
const handleDeleteVehicle = (vehicle: any) => {
  if (!vehicle) {
    return null
  }
  showPopupDeleteVehicle.value = true
  vehicleIsSelectedToDelete.value = vehicle._id
}

const handleViewPlugin = (vehicle: any) => {
  if (!vehicle) {
    return null
  }
  vehicleId.value = vehicle._id
  if (showDrawerListVehiclePlugin.value) {
    showDrawerListVehiclePlugin.value.openViewPlugin(vehicle._id)
  }
}

const headers = [
  {
    field: 'licensePlate',
    headerName: $t('labels.vehicleModule.licensePlate'),
    sortable: true
  },
  {
    field: 'otherCompanyDriver.fullName',
    headerName: $t('labels.driverFullname')
  },
  {
    field: 'otherCompanyDriver.phone',
    headerName: $t('labels.driverPhoneNumber'),
    valueFormatter: (params: any) => {
      const code = params?.data?.otherCompanyDriver.phone.code || ''
      const phone = params?.data?.otherCompanyDriver.phone.phone || ''
      return code ? `(${code}) ${phone}` : phone
    }
  },
  {
    field: 'otherCompanyDriver.idCardNumber',
    headerName: $t('labels.driverIdCardNumber')
  },
  {
    field: 'otherCompanyCoDriver.fullName',
    headerName: 'Họ và tên tài xế phụ'
  },
  {
    field: 'otherCompanyCoDriver.phone',
    headerName: 'Số điện thoại tài xế phụ',
    valueFormatter: (params: any) => {
      const code = params?.data?.otherCompanyCoDriver?.phone?.code || ''
      const phone = params?.data?.otherCompanyCoDriver?.phone?.phone || ''
      return code ? `(${code}) ${phone}` : phone
    }
  },
  {
    field: 'otherCompanyCoDriver.idCardNumber',
    headerName: 'CMND/CCCD/Hộ chiếu tài xế phụ'
  },
  {
    field: 'createdAt',
    headerName: $t('labels.createdAt'),
    sortable: true,
    valueGetter: (params: any) => {
      if (params.data.createdAt) {
        return $formatters.datetime(params.data.createdAt)
      }
    }
  },
  {
    field: 'updatedAt',
    headerName: $t('labels.updatedAt'),
    sortable: true,
    valueGetter: (params: any) => {
      if (params.data.updatedAt) {
        return $formatters.datetime(params.data.updatedAt)
      }
    }
  },
  {
    field: 'action',
    pinned: 'right',
    sortable: false,
    lockPosition: true,
    lockPinned: true,
    headerName: $t('labels.action'),
    cellStyle: { overflow: 'visible' },
    cellRenderer: Actions,
    cellRendererParams: {
      storeKey: 'vehicleDataTable',
      storeName: 'Vehicle',
      items: [
        {
          icon: 'hi-eye-icon',
          label: $t('labels.viewDetail'),
          class: 'text-blue-600 hover:text-blue-500',
          permissions: checkPermission(layoutPermissions.vehicle_management),
          click: handleViewDetail,
          isAll: true
        },
        {
          icon: 'hi-eye-icon',
          label: $t('labels.viewDocument'),
          class: 'text-blue-600 hover:text-blue-500',
          permissions: checkPermission(layoutPermissions.vehicle_document_management),
          click: handleViewDocument
        },
        {
          icon: 'hi-pencil-icon',
          label: $t('labels.edit'),
          class: 'text-blue-600 hover:text-blue-500',
          permissions: checkPermission(layoutPermissions.vehicle_edit) || checkPermission(layoutPermissions.vehicle_administration),
          click: handleEditVehicle
        },
        {
          icon: 'hi-eye-icon',
          label: $t('labels.viewPlugin'),
          class: 'text-blue-600 hover:text-blue-500',
          permissions: checkPermission(layoutPermissions.vehicle_plugin_management),
          click: handleViewPlugin
        },
        {
          icon: 'hi-trash-icon',
          label: $t('labels.delete'),
          class: 'text-error-600 hover:text-error-500',
          permissions: true,
          click: handleDeleteVehicle,
          isArchive: true
        }
      ]
    }
  }
]

const closePopupDeleteVehicle = () => {
  showPopupDeleteVehicle.value = false
  Object.assign(vehicleIsSelectedToDelete, {})
}

const actionDeleteVehicle = () => {
  const api = $urlPartner.vehicle.vehicle.delete
  const vehicle = vehicleIsSelectedToDelete.value
  $api(api, { body: { ids: [vehicle] } }).then((data: any) => {
    const resData: any = data.data.value
    if (resData.success) {
      dataTables.value.refresh()
      showPopupDeleteVehicle.value = false
      process.client && $toast().success(resData.data)
    }
  })
}

const refreshDataTables = () => {
  if (dataTables.value) {
    dataTables.value.refresh()
  }
  getDataCategoryVehicle()
}

const getInfoVehicleEdit = (vehicleId: string) => {
  const apiVehicle = $urlPartner.vehicle.vehicle.getById
  $api(apiVehicle, { uriParams: { ID: vehicleId } }).then((data: any) => {
    const resData: any = data.data.value
    if (resData.success) {
      if (showDrawerEditVehicle.value) {
        showDrawerEditVehicle.value.openEditVehicle({ ...resData.data } as IVehicle)
      }
    }
  })
}
const handleAddVehicle = () => {
  if (showDrawerAddVehicle.value) {
    showDrawerAddVehicle.value.open()
  }
}
const actionChangesStatusVehicle = () => {
  const api = $urlPartner.vehicle.vehicle.changeStatus
  const data = {
    id: dataChangeStatus.value.data._id,
    status: valueStatus.value
  }
  $api(api, { body: data }).then((data: any) => {
    const resData: any = data.data.value
    if (resData.success) {
      process.client && $toast().success($t('labels.updateSuccess'))
      refreshDataTables()
      showPopupChangeStatusVehicle.value = false
    }
  })
}

const dataCategory = ref<Array<{ [key: string]: any }>>([])
const getDataCategoryVehicle = async () => {
  const api = $urlSystem.vehicle.category.getAllList
  try {
    const res = await $api(api)
    const resData: any = res?.data.value
    if (resData.success) {
      if (resData.data) {
        dataCategory.value = resData.data || 0
      }
    }
    return null
  } catch (e: any) {}
}

onMounted(() => {
  getDataCategoryVehicle()
  const id: string = route.query.id ? String(route.query.id) : ''

  const tollStationExpired: string = route.query['toll-station-expired'] ? String(route.query['toll-station-expired']) : ''
  if (id || tollStationExpired) {
    getInfoVehicleEdit(id || tollStationExpired)
    checkDisabledEdit.value = false
  }
})

watch(
  () => route.query,
  () => {
    const id: string = route.query.id ? String(route.query.id) : ''

    const tollStationExpired: string = route.query['toll-station-expired'] ? String(route.query['toll-station-expired']) : ''
    if (id || tollStationExpired) {
      getInfoVehicleEdit(id || tollStationExpired)
      checkDisabledEdit.value = false
    }
  }
)

const getNameTypeVehicle = (vehicleLoad: number | undefined | null) => {
  if (typeof vehicleLoad === 'number') {
    let findCategory: any = null
    findCategory = dataCategory.value.find((item: { [key: string]: any }) => {
      if (item.from <= vehicleLoad && item.to >= vehicleLoad) {
        return true
      }
      return false
    })
    if (!findCategory) {
      findCategory = dataCategory.value.find((item: { [key: string]: any }) => {
        if (item.from <= vehicleLoad && $lodash.isNil(item.to)) {
          return true
        }
        return false
      })
    }
    return findCategory ? findCategory.name || '' : ''
  }
  return ''
}
</script>
